import {sequenceIncludesActiveSubstance} from '@/modules/patients-therapies/functions'
import {useGetSettings} from '@/modules/settings/api'
import {isPatientArrivalEnabled} from '@/modules/settings/functions'
import {PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {useCallback, useMemo} from 'react'

export type JourneyStepStatus = 'pending' | 'in-progress' | 'finished' | 'failed' | 'cancelled'
export type JourneyStatusStep = keyof typeof StandardTherapySequencePhases

export const StandardTherapySequencePhases = {
  DOCTOR_VALIDATION: [
    PatientTherapySequenceStatus.enum.pending_approval,
    PatientTherapySequenceStatus.enum.pending_doctor_validation,
  ],
  VALIDATION: [PatientTherapySequenceStatus.enum.validating],
  PATIENT_ARRIVAL: [PatientTherapySequenceStatus.enum.validating],
  PRODUCTION: [PatientTherapySequenceStatus.enum.production_planning, PatientTherapySequenceStatus.enum.in_production],
  ADMINISTRATION: [
    PatientTherapySequenceStatus.enum.released_for_delivery,
    PatientTherapySequenceStatus.enum.administration,
    PatientTherapySequenceStatus.enum.administered,
  ],
} as const

export const SupportiveTherapySequencePhases = {
  DOCTOR_VALIDATION: [
    PatientTherapySequenceStatus.enum.pending_approval,
    PatientTherapySequenceStatus.enum.pending_doctor_validation,
  ],
  VALIDATION: [PatientTherapySequenceStatus.enum.validating],
  ADMINISTRATION: [
    PatientTherapySequenceStatus.enum.released_for_delivery,
    PatientTherapySequenceStatus.enum.administration,
    PatientTherapySequenceStatus.enum.administered,
  ],
} as const

export const STANDARD_PHASE_ORDER = [
  'DOCTOR_VALIDATION',
  'VALIDATION',
  'PATIENT_ARRIVAL',
  'PRODUCTION',
  'ADMINISTRATION',
] as const satisfies Array<keyof typeof StandardTherapySequencePhases>

// same as the standard phases minus PATIENT_ARRIVAL, used when the doctorRelease setting turns the patient-arrival step off
const StandardTherapySequencePhasesWithoutArrival = {
  DOCTOR_VALIDATION: StandardTherapySequencePhases.DOCTOR_VALIDATION,
  VALIDATION: StandardTherapySequencePhases.VALIDATION,
  PRODUCTION: StandardTherapySequencePhases.PRODUCTION,
  ADMINISTRATION: StandardTherapySequencePhases.ADMINISTRATION,
} as const

export const STANDARD_PHASE_ORDER_WITHOUT_ARRIVAL = [
  'DOCTOR_VALIDATION',
  'VALIDATION',
  'PRODUCTION',
  'ADMINISTRATION',
] as const satisfies Array<keyof typeof StandardTherapySequencePhases>

export const SUPPORTIVE_PHASE_ORDER = ['DOCTOR_VALIDATION', 'VALIDATION', 'ADMINISTRATION'] as const satisfies Array<
  keyof typeof SupportiveTherapySequencePhases
>

const PHASE_COLORS = {
  DOCTOR_VALIDATION: 'black',
  VALIDATION: 'blue.solid',
  PATIENT_ARRIVAL: 'orange.solid',
  PRODUCTION: 'purple.solid',
  ADMINISTRATION: 'green.solid',
} as const

const FAILED_STATUS_PHASE_MAP: Partial<Record<string, 'PRODUCTION' | 'ADMINISTRATION' | 'DOCTOR_VALIDATION'>> = {
  [PatientTherapySequenceStatus.enum.budget_not_approved]: 'DOCTOR_VALIDATION',
  [PatientTherapySequenceStatus.enum.discarded]: 'PRODUCTION',
  [PatientTherapySequenceStatus.enum.returned]: 'ADMINISTRATION',
}

// Statuses that mean the phase is over but the next one has not started yet: the preparation is
// finished once the label is created (QA & dispatch), so production shows finished and
// administration still pending.
const COMPLETED_PHASE_STATUS_MAP: Partial<Record<string, JourneyStatusStep>> = {
  [PatientTherapySequenceStatus.enum.qa_dispatch]: 'PRODUCTION',
}

function getPhasesAndOrder(sequence: PatientTherapySequence, patientArrivalEnabled = true) {
  const isActiveSubstance = sequenceIncludesActiveSubstance(sequence)
  if (!isActiveSubstance) {
    return {
      phases: SupportiveTherapySequencePhases,
      orderedSteps: SUPPORTIVE_PHASE_ORDER as JourneyStatusStep[],
    }
  }
  if (!patientArrivalEnabled) {
    return {
      phases: StandardTherapySequencePhasesWithoutArrival,
      orderedSteps: STANDARD_PHASE_ORDER_WITHOUT_ARRIVAL as readonly JourneyStatusStep[],
    }
  }
  return {
    phases: StandardTherapySequencePhases,
    orderedSteps: STANDARD_PHASE_ORDER as JourneyStatusStep[],
  }
}

function getStepStatusHelper(
  step: JourneyStatusStep,
  orderedSteps: readonly JourneyStatusStep[],
  isFailed: boolean,
  currentPhaseIndex: number,
  failedAtPhaseIndex: number,
  isCancelled: boolean,
  cancelledAtPhaseIndex: number
): JourneyStepStatus {
  const index = orderedSteps.indexOf(step)

  if (isCancelled) {
    if (index === cancelledAtPhaseIndex) return 'cancelled'
    return index < cancelledAtPhaseIndex ? 'finished' : 'pending'
  }

  if (isFailed) {
    if (index === failedAtPhaseIndex) return 'failed'
    if (index < failedAtPhaseIndex) return 'finished'
    return 'pending'
  }

  if (currentPhaseIndex > index) return 'finished'
  if (currentPhaseIndex === index) return 'in-progress'
  return 'pending'
}

function getStepStateHelper(
  step: JourneyStatusStep,
  orderedSteps: readonly JourneyStatusStep[],
  isFailed: boolean,
  currentPhaseIndex: number,
  failedAtPhaseIndex: number,
  isCancelled: boolean,
  cancelledAtPhaseIndex: number
) {
  const status = getStepStatusHelper(
    step,
    orderedSteps,
    isFailed,
    currentPhaseIndex,
    failedAtPhaseIndex,
    isCancelled,
    cancelledAtPhaseIndex
  )
  return {
    status,
    color:
      status === 'failed' || status === 'cancelled' ? 'red.solid' : PHASE_COLORS[step as keyof typeof PHASE_COLORS],
  }
}

export function extractSteps(sequence: PatientTherapySequence, patientArrivalEnabled = true) {
  const {phases, orderedSteps} = getPhasesAndOrder(sequence, patientArrivalEnabled)
  const {isFailed, isCancelled, currentPhaseIndex, failedAtPhaseIndex, cancelledAtPhaseIndex} = buildJourneyState(
    sequence,
    phases,
    orderedSteps
  )
  return orderedSteps.map(step => ({
    label: step.toLowerCase(),
    step,
    ...getStepStateHelper(
      step,
      orderedSteps,
      isFailed,
      currentPhaseIndex,
      failedAtPhaseIndex,
      isCancelled,
      cancelledAtPhaseIndex
    ),
  }))
}

// a production cancel leaves an order behind, so it is shown cancelled at the production step;
// anything cancelled before production stops at the first step
function getCancelledPhaseIndex(sequence: PatientTherapySequence, orderedSteps: readonly JourneyStatusStep[]) {
  if (sequence.orderId && orderedSteps.includes('PRODUCTION')) return orderedSteps.indexOf('PRODUCTION')
  return 0
}

function buildJourneyState(
  sequence: PatientTherapySequence,
  phases: Record<string, readonly string[]>,
  orderedSteps: readonly string[]
) {
  const currentStatus = sequence.status

  if (!currentStatus) {
    return {
      currentPhaseKey: orderedSteps[0] as JourneyStatusStep,
      currentPhaseIndex: 0,
      isFailed: false,
      isCancelled: false,
      failedAtPhaseIndex: -1,
      cancelledAtPhaseIndex: -1,
    }
  }

  if (currentStatus === PatientTherapySequenceStatus.enum.cancelled) {
    const cancelledAtPhaseIndex = getCancelledPhaseIndex(sequence, orderedSteps as JourneyStatusStep[])
    return {
      currentPhaseKey: orderedSteps[cancelledAtPhaseIndex] as JourneyStatusStep,
      currentPhaseIndex: cancelledAtPhaseIndex,
      isFailed: false,
      isCancelled: true,
      failedAtPhaseIndex: -1,
      cancelledAtPhaseIndex,
    }
  }

  const failedPhaseRaw = FAILED_STATUS_PHASE_MAP[currentStatus]
  const failedPhase =
    failedPhaseRaw && orderedSteps.includes(failedPhaseRaw)
      ? failedPhaseRaw
      : failedPhaseRaw
        ? orderedSteps[orderedSteps.length - 1]
        : null

  if (failedPhase) {
    const failedAtPhaseIndex = orderedSteps.indexOf(failedPhase)
    return {
      currentPhaseKey: failedPhase as JourneyStatusStep,
      currentPhaseIndex: failedAtPhaseIndex,
      isFailed: true,
      isCancelled: false,
      failedAtPhaseIndex,
      cancelledAtPhaseIndex: -1,
    }
  }

  // Half a step past the completed phase: it reads finished while the next one stays pending.
  const completedPhase = COMPLETED_PHASE_STATUS_MAP[currentStatus]
  if (completedPhase && orderedSteps.includes(completedPhase)) {
    return {
      currentPhaseKey: completedPhase,
      currentPhaseIndex: orderedSteps.indexOf(completedPhase) + 0.5,
      isFailed: false,
      isCancelled: false,
      failedAtPhaseIndex: -1,
      cancelledAtPhaseIndex: -1,
    }
  }

  const currentPhaseKey =
    orderedSteps.find(key => {
      const statuses = phases[key] as string[]
      return statuses.includes(currentStatus)
    }) ?? orderedSteps[0]

  const currentPhaseIndex = orderedSteps.indexOf(currentPhaseKey)

  const isFullyComplete = currentStatus === PatientTherapySequenceStatus.enum.administered

  return {
    currentPhaseKey: currentPhaseKey as JourneyStatusStep,
    currentPhaseIndex: isFullyComplete ? orderedSteps.length : currentPhaseIndex,
    isFailed: false,
    isCancelled: false,
    failedAtPhaseIndex: -1,
    cancelledAtPhaseIndex: -1,
  }
}

export function useSequenceJourneyState(sequence: PatientTherapySequence) {
  const {settings} = useGetSettings()
  const patientArrivalEnabled = isPatientArrivalEnabled(settings)
  const {phases, orderedSteps} = useMemo(
    () => getPhasesAndOrder(sequence, patientArrivalEnabled),
    [sequence, patientArrivalEnabled]
  )
  const {isFailed, isCancelled, currentPhaseIndex, currentPhaseKey, failedAtPhaseIndex, cancelledAtPhaseIndex} =
    useMemo(() => buildJourneyState(sequence, phases, orderedSteps), [sequence, phases, orderedSteps])

  const getStepState = useCallback(
    (step: JourneyStatusStep) =>
      getStepStateHelper(
        step,
        orderedSteps,
        isFailed,
        currentPhaseIndex,
        failedAtPhaseIndex,
        isCancelled,
        cancelledAtPhaseIndex
      ),
    [orderedSteps, isFailed, currentPhaseIndex, failedAtPhaseIndex, isCancelled, cancelledAtPhaseIndex]
  )

  return {
    getStepState,
    currentPhaseKey,
    currentPhaseIndex,
    isFailed,
    orderedSteps,
  }
}
